import { useEffect, useMemo, useState } from 'react'
import Select from 'react-select'
import * as Yup from 'yup'
import { Formik, Form } from 'formik'
import DatePicker from 'react-datepicker'
import { useTranslation } from 'react-i18next'

import { useIsSwiftlaneBuildingConnected } from '@/api/property-keyless/swiftlane/use-is-swiftlane-building-connected'
import { Alert } from '@/components/ui/alert'
import { useSnackbar } from '@/helpers/hooks/useSnackbar'

import API from '../../../../api/api'
import ConfirmPopup from '../../../Popups/ConfirmPopup'
import { saltoSpaceDropdownStyles, getConfirmationText } from '../helpers'
import Loader from '../../../../components/Loader/Loader'

const getCurrentDate = () => new Date()
const isDateValid = (endDate) => endDate && new Date(endDate) > getCurrentDate()

const SwiftlaneSchema = Yup.object().shape({
  selectedAccessLevels: Yup.array()
    .of(
      Yup.object().shape({
        id: Yup.string().required(),
        name: Yup.string().required(),
      }),
    )
    .nullable(),
  expirationDate: Yup.number(),
})

const SwiftlaneSection = ({ isAvailable, buildingId, clientData }) => {
  const { t } = useTranslation(['clients-legacy', 'clients', 'common'])
  const { displaySnackbar } = useSnackbar()
  const { isBuildingConnected, isLoading: isLoadingBuildingConnection } =
    useIsSwiftlaneBuildingConnected(buildingId)

  const expirationDateInitialValue = isDateValid(clientData.leaseEnd)
    ? clientData.leaseEnd
    : isDateValid(clientData.moveOutDate)
      ? clientData.moveOutDate
      : undefined

  const [isConnectUserPopupVisible, setIsConnectUserPopupVisible] = useState(false)
  const [isCreateNewUserPopupVisible, setIsCreateNewUserPopupVisible] = useState(false)

  const [uniqueSwiftlaneUsers, setUniqueSwiftlaneUsers] = useState([])
  const [selectedUserId, setSelectedUserId] = useState(null)

  const [isConnecting, setIsConnecting] = useState(false)
  const [isCreatingNewUser, setIsCreatingNewUser] = useState(false)

  const [isUserConnectedToSwiftlane, setIsUserConnectedToSwiftlane] = useState(false)
  const [isSwiftlaneLoading, setIsSwiftlaneLoading] = useState(false)

  const [swiftlaneAccessLevels, setSwiftlaneAccessLevels] = useState([])
  const [isSubmittingSwiftlane, setIsSubmittingSwiftlane] = useState(false)

  const [swiftlaneInitialValues, setSwiftlaneInitialValues] = useState({
    accessLevels: [],
    expirationDate: expirationDateInitialValue,
  })

  const {
    user: { email },
  } = clientData

  const initialValues = useMemo(
    () => ({
      selectedAccessLevels: swiftlaneInitialValues.accessLevels || [],
      expirationDate: swiftlaneInitialValues.expirationDate,
      confirmed: false,
    }),
    [swiftlaneInitialValues.accessLevels, swiftlaneInitialValues.expirationDate],
  )

  const applyMatchingUsers = async (arr) => {
    setUniqueSwiftlaneUsers(arr)

    const selectedUserId = arr.length === 1 ? arr[0]?.id : null

    setSelectedUserId(selectedUserId ?? null)
    setIsUserConnectedToSwiftlane(!!selectedUserId)
    if (arr.length === 1) {
      try {
        await API.mapSwiftlaneUserToElos(arr[0].id, clientData.user.id)
      } catch {
        displaySnackbar(t('clients-legacy:profileView.integrations.mapSwiftlaneUserError'), true)
      }
    }
  }

  const handleCreateNewUser = async () => {
    try {
      setIsCreatingNewUser(true)
      const res = await API.createSwiftlaneUser(email, buildingId)

      const createdId = res?.data?.id
      if (createdId) setSelectedUserId(createdId)

      setSwiftlaneInitialValues({
        accessLevels: [],
        expirationDate: expirationDateInitialValue,
      })
      setIsUserConnectedToSwiftlane(true)
      displaySnackbar(t('clients:keylessAccess.swiftlane.toasts.createSuccess'))
    } catch {
      // Note: BE creates the Swiftlane user first and the Intercom Entry second. A duplicate
      // "FIRSTNAME L." makes the Entry fail, so BE answers 500 although the user is created and
      // linked to the resident. Re-read the resident (the same source the New UI uses to decide
      // "connected") so a PM isn't told it failed and retries; a real failure leaves no resident.
      try {
        const { data: createdResident } = await API.getSwiftlaneUserGroupsByUserId(
          buildingId,
          clientData.user.id,
        )

        if (createdResident) {
          setSwiftlaneInitialValues({
            accessLevels: [],
            expirationDate: expirationDateInitialValue,
          })
          setIsUserConnectedToSwiftlane(true)
          displaySnackbar(t('clients:keylessAccess.swiftlane.toasts.createIntercomWarning'), true)
          return
        }
      } catch {
        // Falls through to the original error below.
      }
      displaySnackbar(t('clients-legacy:profileView.integrations.swiftlane.createError'), true)
    } finally {
      setIsCreatingNewUser(false)
      setIsCreateNewUserPopupVisible(false)
    }
  }

  const handleConnectToSwiftlane = async () => {
    try {
      setIsConnecting(true)
      const response = await API.getSwiftlaneUsersMatching(email, buildingId)
      const arr = response.data ?? []

      if (arr.length === 1) {
        applyMatchingUsers(arr)
        return
      }

      if (!selectedUserId) {
        setUniqueSwiftlaneUsers(arr)
        displaySnackbar(
          t('clients-legacy:profileView.integrations.swiftlane.accountMismatchError'),
          true,
        )
        return
      }

      const exists = arr.some((user) => user?.id === selectedUserId)
      if (!exists) {
        setUniqueSwiftlaneUsers(arr)
        setSelectedUserId(null)
        displaySnackbar(
          t('clients-legacy:profileView.integrations.swiftlane.userNoLongerAvailable'),
          true,
        )
        return
      }

      setSwiftlaneInitialValues({
        accessLevels: [],
        expirationDate: expirationDateInitialValue,
      })
      setIsUserConnectedToSwiftlane(true)
    } catch {
      displaySnackbar(t('clients-legacy:profileView.integrations.swiftlaneConnectError'), true)
    } finally {
      setIsConnecting(false)
    }
  }

  const handleSwiftlaneSubmit = async (selectedAccessLevels, expirationDate, confirmed) => {
    if (!confirmed) return

    const userGroupIds = (selectedAccessLevels || []).map((x) => x.id)

    setIsSubmittingSwiftlane(true)
    try {
      setIsConnectUserPopupVisible(false)

      await API.updateSwiftlaneUserGroups(buildingId, email, userGroupIds, expirationDate)

      displaySnackbar(t('clients-legacy:profileView.integrations.accessGranted'))
      setSwiftlaneInitialValues({
        accessLevels: selectedAccessLevels || [],
        expirationDate,
      })
    } catch {
      displaySnackbar(t('clients-legacy:profileView.integrations.grantAccessError'), true)
    } finally {
      setIsSubmittingSwiftlane(false)
    }
  }

  const fetchSwiftlaneAccessLevels = async () => {
    try {
      const { data } = await API.getSwiftlaneUserGroupsByBuildingId(buildingId)
      setSwiftlaneAccessLevels(data)
    } catch {
      displaySnackbar(t('clients-legacy:profileView.integrations.somethingWentWrong'), true)
      setSwiftlaneAccessLevels([])
    }
  }

  const fetchUserConnectedGroups = async (userId) => {
    if (!userId) {
      setSwiftlaneInitialValues({
        accessLevels: [],
        expirationDate: expirationDateInitialValue,
      })
      return
    }

    try {
      const { data } = await API.getSwiftlaneUserGroupsByUserId(buildingId, userId)
      setSwiftlaneInitialValues({
        accessLevels: data?.groups || [],
        expirationDate: data.expirationDate
          ? new Date(data.expirationDate).getTime()
          : expirationDateInitialValue
            ? new Date(expirationDateInitialValue).getTime()
            : undefined,
      })
    } catch {
      setSwiftlaneInitialValues({
        accessLevels: [],
        expirationDate: expirationDateInitialValue,
      })
    }
  }

  const checkIfUserIsConnectedToSwiftlane = async () => {
    try {
      setIsSwiftlaneLoading(true)

      const matchingResponse = await API.getSwiftlaneUsersMatching(email, buildingId)
      const arr = Array.isArray(matchingResponse.data) ? matchingResponse.data : []

      applyMatchingUsers(arr)
    } catch (error) {
      console.error(error)
      setUniqueSwiftlaneUsers([])
      setSelectedUserId(null)
      setIsUserConnectedToSwiftlane(false)
    } finally {
      setIsSwiftlaneLoading(false)
    }
  }

  useEffect(() => {
    if (isUserConnectedToSwiftlane) fetchSwiftlaneAccessLevels()
  }, [isUserConnectedToSwiftlane, buildingId])

  useEffect(() => {
    if (isUserConnectedToSwiftlane) fetchUserConnectedGroups(clientData.user.id)
  }, [isUserConnectedToSwiftlane, selectedUserId, buildingId])

  useEffect(() => {
    if (isAvailable) checkIfUserIsConnectedToSwiftlane()
  }, [isAvailable])

  if (!isAvailable) return null

  if (isLoadingBuildingConnection) {
    return (
      <div className="information-element information-element-column">
        <Loader />
      </div>
    )
  }

  if (!isBuildingConnected) {
    return (
      <div className="information-element information-element-column">
        <Alert
          type="error"
          title={t('clients:keylessAccess.swiftlane.buildingNotConnected.title')}
          message={t('clients:keylessAccess.swiftlane.buildingNotConnected.message')}
        />
      </div>
    )
  }

  return (
    <div className="information-element">
      {isUserConnectedToSwiftlane ? (
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={SwiftlaneSchema}
          onSubmit={({ selectedAccessLevels, expirationDate, confirmed }) =>
            handleSwiftlaneSubmit(selectedAccessLevels, expirationDate, confirmed)
          }
        >
          {({ values, errors, touched, setFieldValue, handleSubmit, resetForm }) => {
            const hasError = (field) => errors[field] && touched[field]

            const accessLevelNames = Array.isArray(values.selectedAccessLevels)
              ? values.selectedAccessLevels.map((x) => x?.name).filter(Boolean)
              : []
            const accessLevelIds = Array.isArray(values.selectedAccessLevels)
              ? values.selectedAccessLevels.map((x) => x?.id).filter(Boolean)
              : []

            const confirmationText = getConfirmationText(t, clientData, {
              ...values,
              selectedGroups: accessLevelIds,
              selectedGroupsNames: accessLevelNames,
              selectedAccessLevelsNames: accessLevelNames,
            })

            const onChangeAccessLevel = (selectedOptions) => {
              setFieldValue('selectedAccessLevels', selectedOptions || [])
            }

            const onChangeExpirationDate = (selectedDate) => {
              const date = new Date(selectedDate)
              return setFieldValue('expirationDate', date.getTime() || '')
            }

            const handleConfirm = () => {
              setFieldValue('confirmed', true)
              setIsConnectUserPopupVisible(true)
            }

            return (
              <Form>
                <div className="columns">
                  <div className="description-column">
                    <div className="element-name element-margin-top">
                      {t('clients-legacy:profileView.integrations.swiftlane.label')}
                    </div>

                    <div className="dropdown-buttons-container">
                      <div className="dropdown-buttons-container-row">
                        <div className="dropdown-container">
                          <div className="dropdown-header">
                            {t('clients-legacy:profileView.integrations.accessLevelLabel')}
                          </div>
                          <Select
                            styles={saltoSpaceDropdownStyles(hasError('selectedAccessLevels'))}
                            className="basic-multi-select"
                            classNamePrefix="select"
                            options={swiftlaneAccessLevels}
                            getOptionLabel={(option) => option.name}
                            getOptionValue={(option) => option.id}
                            value={values.selectedAccessLevels}
                            onChange={onChangeAccessLevel}
                            placeholder={t(
                              'clients:keylessAccess.saltoSpace.accessLevel.placeholder',
                            )}
                            isClearable
                            isMulti
                            closeMenuOnSelect={false}
                          />
                          {hasError('selectedAccessLevels') && (
                            <div className="error-validation">{errors.selectedAccessLevels}</div>
                          )}
                        </div>
                        <div className="dropdown-container">
                          <div className="dropdown-header">
                            {t('clients-legacy:profileView.integrations.expirationDateLabel')}
                          </div>
                          <DatePicker
                            className={`dropdown-button ${
                              hasError('expirationDate') && 'dropdown-button-error-color'
                            }`}
                            placeholderText={t(
                              'clients-legacy:profileView.integrations.dateFormatPlaceholder',
                            )}
                            selected={values.expirationDate || null}
                            onChange={(selectedDate) => onChangeExpirationDate(selectedDate)}
                            minDate={getCurrentDate()}
                            isClearable
                          />
                          {hasError('expirationDate') && (
                            <div className="error-validation">{errors.expirationDate}</div>
                          )}
                        </div>
                      </div>
                    </div>

                    <button
                      type="button"
                      className={`confirm-button button-primary button ${
                        isSubmittingSwiftlane ? 'is-loading' : ''
                      }`}
                      disabled={isSubmittingSwiftlane}
                      onClick={handleConfirm}
                    >
                      {t('common:actions.confirm')}
                    </button>
                  </div>
                </div>

                <ConfirmPopup
                  title={t('common:confirmPopup.caution')}
                  body={confirmationText}
                  cancelText={t('common:actions.close')}
                  confirmText={t('clients-legacy:profileView.integrations.yesConfirm')}
                  isOpen={isConnectUserPopupVisible}
                  cancelCallback={() => {
                    resetForm()
                    setIsConnectUserPopupVisible(false)
                  }}
                  confirmCallback={handleSubmit}
                  className={'is-caution'}
                />
              </Form>
            )
          }}
        </Formik>
      ) : isSwiftlaneLoading ? (
        <div className="information-element-row">
          <Loader />
        </div>
      ) : (
        <div className="information-element-row">
          <div className="element-name">
            {t('clients-legacy:profileView.integrations.swiftlane.label')}
          </div>

          {uniqueSwiftlaneUsers.length > 0 ? (
            <div className="columns">
              <div className="column column-right">
                <Select
                  styles={{
                    ...saltoSpaceDropdownStyles(),
                    control: (styles) => ({ ...styles, minWidth: '250px' }),
                  }}
                  options={uniqueSwiftlaneUsers.map((user) => ({
                    label: user.name || user.email,
                    value: user.id,
                  }))}
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.value}
                  value={
                    uniqueSwiftlaneUsers
                      .map((user) => ({ label: user.name || user.email, value: user.id }))
                      .find((user) => user.value === selectedUserId) || null
                  }
                  onChange={(selectedOption) =>
                    setSelectedUserId(selectedOption ? selectedOption.value : null)
                  }
                  placeholder={t('clients-legacy:profileView.integrations.selectUserPlaceholder')}
                  isClearable
                  closeMenuOnSelect
                />
                <button
                  className={`button button-primary ${isConnecting ? 'is-loading' : ''}`}
                  onClick={handleConnectToSwiftlane}
                  disabled={isConnecting || !selectedUserId}
                >
                  {isConnecting
                    ? t('clients-legacy:profileView.integrations.connectingEllipsis')
                    : t('clients:keylessAccess.swiftlane.connectToSwiftlane')}
                </button>
              </div>
            </div>
          ) : (
            <div className="columns">
              <div className="column column-right">
                <button
                  className={`button button-primary ${isCreatingNewUser ? 'is-loading' : ''}`}
                  onClick={() => setIsCreateNewUserPopupVisible(true)}
                  disabled={isConnecting || isCreatingNewUser}
                >
                  {isCreatingNewUser
                    ? t('clients-legacy:profileView.integrations.creatingEllipsis')
                    : t('clients-legacy:profileView.integrations.createNewUser')}
                </button>
                <span>{t('clients:keylessAccess.notConnected.or')}</span>
                <button
                  className={`button button-primary ${isConnecting ? 'is-loading' : ''}`}
                  onClick={handleConnectToSwiftlane}
                  disabled={isConnecting || isCreatingNewUser}
                >
                  {isConnecting
                    ? t('clients-legacy:profileView.integrations.connectingEllipsis')
                    : t('clients-legacy:profileView.integrations.connectExistingAccount')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      <ConfirmPopup
        title={t('common:confirmPopup.caution')}
        body={t('clients:keylessAccess.swiftlane.createUser.confirmBody')}
        isOpen={isCreateNewUserPopupVisible}
        cancelText={t('common:actions.close')}
        confirmText={t('clients-legacy:profileView.integrations.yesCreate')}
        cancelCallback={() => setIsCreateNewUserPopupVisible(false)}
        confirmCallback={handleCreateNewUser}
        className="is-caution"
        isLoading={isCreatingNewUser}
      />
    </div>
  )
}

export default SwiftlaneSection
